/**
 * "My questions" (#1681, mockup screen 4) — what you've reported through the
 * Help Desk and where each one stands, in plain words. Nothing closes until
 * you say it's fixed: "Fixed — check it" asks you, and "Still wrong" sends it
 * back with what you say.
 *
 * Lives in the top-bar Help Desk panel (toolbar `.hck-*` / `.hmq-*` classes).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useConfirmCase, useMyCases, type MyCase } from "@/lib/use-my-cases";
import type { ListedAction } from "@/components/help-desk-review";

/** #1947 Phase 3: fixes you sent for an OK. A ✓ here is the only signal. */
function useMyActions() {
    return useQuery<{ actions: ListedAction[] }>({
        queryKey: ["help-desk", "my-actions"],
        queryFn: () => api.get(`/api/help-desk/my-actions`),
        staleTime: 30_000,
        retry: false,
    });
}

function MyFixes({ actions }: { actions: ListedAction[] }) {
    if (actions.length === 0) return null;
    const waiting = actions.filter((a) => a.status === "waiting");
    const decided = actions.filter((a) => a.status !== "waiting");
    return (
        <section className="hmq-sec" data-testid="my-fixes">
            {waiting.length > 0 && (
                <>
                    <h4 className="hmq-h">Waiting on {waiting[0].approver ?? "an Admin"}</h4>
                    {waiting.map((a) => (
                        <div key={a.id} className="hck-card">
                            <div className="hck-title">{a.preview.title}</div>
                            <div className="hck-detail">{a.preview.lines[0]?.replace(/\*\*/g, "")}</div>
                            <div className="hmq-meta">You sent it {ago(a.askedAt)}</div>
                        </div>
                    ))}
                </>
            )}
            {decided.map((a) => (
                <div key={a.id} className="hck-card">
                    <div className="hck-title">
                        {a.status === "done" ? "✓ " : ""}
                        {a.preview.title}
                    </div>
                    <div className="hck-detail">
                        {a.status === "done"
                            ? `${a.result?.text ?? "Done."}${a.decidedBy ? ` OK'd by ${a.decidedBy}.` : ""}`
                            : a.status === "declined"
                              ? `Not done${a.decidedBy ? `: ${a.decidedBy} said no` : ""}${a.decisionNote ? `. "${a.decisionNote}"` : "."}`
                              : a.status === "undone"
                                ? (a.result?.text ?? "Put back.")
                                : (a.error ?? "It didn't work.")}
                    </div>
                </div>
            ))}
        </section>
    );
}

function ago(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function CheckCard({ c }: { c: MyCase }) {
    const confirm = useConfirmCase();
    const [wrong, setWrong] = useState(false);
    const [note, setNote] = useState("");
    const [result, setResult] = useState<string | null>(null);
    const answer = (ok: boolean) =>
        confirm.mutate(
            { id: c.id, ok, note: ok ? undefined : note },
            { onSuccess: (r) => setResult(r.message), onError: (e) => setResult(e.message) },
        );
    return (
        <div className="hck-card" data-testid={`my-case-${c.id}`}>
            <div className="hck-title">{c.title}</div>
            <div className="hck-detail">{c.where}</div>
            <div className="hmq-meta">
                You asked {ago(c.askedAt)}
                {c.link && (
                    <>
                        {" · "}
                        <Link to={c.link}>Open it</Link>
                    </>
                )}
            </div>
            {result ? (
                <div className="hck-note">{result}</div>
            ) : wrong ? (
                <>
                    <label className="hck-title" htmlFor={`hmq-note-${c.id}`}>
                        What&apos;s still wrong?
                    </label>
                    <textarea
                        id={`hmq-note-${c.id}`}
                        className="hck-text"
                        rows={2}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                    />
                    <div className="hck-row">
                        <button
                            type="button"
                            className="hck-fix"
                            disabled={confirm.isPending}
                            onClick={() => answer(false)}
                        >
                            Send it back
                        </button>
                        <button type="button" className="hck-alt" onClick={() => setWrong(false)}>
                            Cancel
                        </button>
                    </div>
                </>
            ) : (
                <div className="hck-row">
                    <button type="button" className="hck-fix" disabled={confirm.isPending} onClick={() => answer(true)}>
                        Looks right
                    </button>
                    <button
                        type="button"
                        className="hck-alt"
                        disabled={confirm.isPending}
                        onClick={() => setWrong(true)}
                    >
                        Still wrong
                    </button>
                </div>
            )}
        </div>
    );
}

export function MyQuestions() {
    const q = useMyCases();
    const fixes = useMyActions();
    const myFixes = Array.isArray(fixes.data?.actions) ? fixes.data.actions : [];
    const [showDone, setShowDone] = useState(false);
    if (q.isLoading) return <div className="hb gw">Getting your questions…</div>;
    if (q.isError || !q.data) {
        return <div className="hb gw">I couldn&apos;t load your questions just now. Try again in a moment.</div>;
    }
    const { cases, owner } = q.data;
    const check = cases.filter((c) => c.group === "check");
    const open = cases.filter((c) => c.group === "open");
    const done = cases.filter((c) => c.group === "done");
    if (cases.length === 0 && myFixes.length === 0) {
        return (
            <div className="hb gw">
                Nothing you&apos;ve asked is waiting on anyone. When you send something to {owner ?? "the office"} from
                the Help Desk, it shows up here until it&apos;s fixed.
            </div>
        );
    }
    return (
        <div className="hck" data-testid="my-questions">
            <MyFixes actions={myFixes} />
            {check.length > 0 && (
                <section className="hmq-sec">
                    <h4 className="hmq-h ok">Fixed — check it</h4>
                    {check.map((c) => (
                        <CheckCard key={c.id} c={c} />
                    ))}
                </section>
            )}
            {open.length > 0 && (
                <section className="hmq-sec">
                    <h4 className="hmq-h">With {owner ?? "the office"}</h4>
                    {open.map((c) => (
                        <div key={c.id} className="hck-card" data-testid={`my-case-${c.id}`}>
                            <div className="hck-title">
                                {c.title}
                                {c.urgent && <span className="hmq-urgent">urgent</span>}
                            </div>
                            <div className="hck-detail">
                                <b>Where it&apos;s at:</b> {c.where}
                            </div>
                            <div className="hmq-meta">
                                You asked {ago(c.askedAt)}
                                {c.link && (
                                    <>
                                        {" · "}
                                        <Link to={c.link}>Open it</Link>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </section>
            )}
            {done.length > 0 && (
                <section className="hmq-sec">
                    <div className="hck-checked">
                        <span>Done · {done.length}</span>
                        <button
                            type="button"
                            className="hck-link"
                            aria-expanded={showDone}
                            onClick={() => setShowDone((v) => !v)}
                        >
                            {showDone ? "Hide" : "Show"}
                        </button>
                    </div>
                    {showDone && (
                        <ul className="hck-list">
                            {done.map((c) => (
                                <li key={c.id} data-ok="true">
                                    <span>
                                        <b>{c.title}</b> — {c.where}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            )}
        </div>
    );
}
